import { useCallback, useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { loginWithGoogle } from '../api/auth'
import { AUTH_PATHS } from '../constants/auth'
import { resolveAuthRedirect } from '../helpers/authRedirect'
import {
  googleAuthErrorMessage,
  mountGoogleSignInButton,
  requestGoogleIdToken,
  resolveGoogleClientId,
} from '../helpers/googleAuth'
import { useAuth } from './useAuth'

export function useGoogleSignIn(enabled: boolean, settingsClientId?: string | null) {
  const navigate = useNavigate()
  const location = useLocation()
  const { applyTokens } = useAuth()
  const hostRef = useRef<HTMLDivElement>(null)
  const [error, setError] = useState<string | null>(null)
  const [pending, setPending] = useState(false)
  const [fallback, setFallback] = useState(false)
  const clientId = resolveGoogleClientId(settingsClientId)
  const redirectState = useRef(location.state)
  redirectState.current = location.state

  const finishWithGoogleToken = useCallback(
    async (idToken: string) => {
      setPending(true)
      setError(null)
      try {
        const tokens = await loginWithGoogle(idToken)
        applyTokens(tokens)
        navigate(resolveAuthRedirect(redirectState.current, AUTH_PATHS.home), {
          replace: true,
        })
      } catch (err) {
        setError(googleAuthErrorMessage(err))
        setPending(false)
      }
    },
    [applyTokens, navigate],
  )

  useEffect(() => {
    if (!enabled || !clientId) return
    const host = hostRef.current
    if (!host) return

    let cancelled = false
    let unmount: (() => void) | undefined

    void mountGoogleSignInButton(
      host,
      async (idToken) => {
        if (cancelled) return
        await finishWithGoogleToken(idToken)
      },
      clientId,
    )
      .then((cleanup) => {
        if (cancelled) {
          cleanup()
          return
        }
        unmount = cleanup
      })
      .catch(() => {
        if (cancelled) return
        setFallback(true)
      })

    return () => {
      cancelled = true
      unmount?.()
    }
  }, [clientId, enabled, finishWithGoogleToken])

  async function onFallbackClick() {
    try {
      setError(null)
      const idToken = await requestGoogleIdToken(clientId)
      await finishWithGoogleToken(idToken)
    } catch (err) {
      setError(googleAuthErrorMessage(err))
    }
  }

  return {
    hostRef,
    googleError: error,
    googlePending: pending,
    useFallbackButton: !clientId || fallback,
    onFallbackClick,
  }
}
